// @/components/reusable/confirm-dialog.jsx
// V2 Confirmation Dialog - Consistent styling across the app
// All v2 dialogs share: Alert base, <h4> title, <Text> description, consistent button layout

import React from "react";
import PropTypes from "prop-types";

// Reusable components
import { Alert, AlertTitle, AlertDescription, AlertActions } from "@/components/reusable/alert";
import { Button } from "@/components/reusable/button";

// ============================================================================
// COMPONENT
// ============================================================================

/**
 * ConfirmDialog - Reusable confirmation dialog with consistent styling
 *
 * Props:
 * - isOpen: boolean - Whether dialog is visible
 * - onClose: () => void - Called when dialog should close
 * - onConfirm: () => void - Called when destructive action is confirmed
 * - title: string - Dialog title
 * - description: string - Dialog description (supports HTML)
 * - cancelText: string - Text for cancel button (default: "Cancel")
 * - confirmText: string - Text for confirm button (default: "Confirm")
 * - variant: "danger" | "warning" | "default" - Button color variant (default: "danger")
 * - disabled: boolean - Disable buttons during processing
 */
const ConfirmDialog = ({
  isOpen,
  onClose,
  onConfirm,
  title,
  description,
  cancelText = "Cancel",
  confirmText = "Confirm",
  variant = "danger",
  disabled = false,
}) => {
  return (
    <Alert
      open={isOpen}
      onClose={disabled ? () => { } : onClose}
      size="xl"
      zIndex={100}
    >
      <AlertTitle>{title}</AlertTitle>
      {typeof description === "string" && description.includes("<") ? (
        <AlertDescription dangerouslySetInnerHTML={{ __html: description }} />
      ) : (
        <AlertDescription>{description}</AlertDescription>
      )}
      <AlertActions>
        <Button onClick={onClose} variant="plain" disabled={disabled}>
          {cancelText}
        </Button>
        <Button
          onClick={onConfirm}
          variant={variant === "warning" ? "warning" : variant === "default" ? "solid" : "danger"}
          disabled={disabled}
        >
          {confirmText}
        </Button>
      </AlertActions>
    </Alert>
  );
};

ConfirmDialog.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
  title: PropTypes.string.isRequired,
  description: PropTypes.oneOfType([PropTypes.string, PropTypes.node]).isRequired,
  cancelText: PropTypes.string,
  confirmText: PropTypes.string,
  variant: PropTypes.oneOf(["danger", "warning", "default"]),
  disabled: PropTypes.bool,
};

export default ConfirmDialog;
